import { Link } from "react-router-dom";
import { Lock, ShieldCheck, Truck, Phone, Star, PackageCheck } from "lucide-react";
import { SHOP } from "@/lib/shop";
import { T } from "@/lib/i18n";

const card = "flex h-8 items-center justify-center rounded-[4px] bg-white px-2";

function Visa() {
  return <span className={card} title="Visa"><svg viewBox="0 0 48 16" className="h-3.5 w-auto"><text x="0" y="14" fontFamily="Arial Black,Arial" fontWeight="900" fontStyle="italic" fontSize="16" fill="#1A1F71">VISA</text></svg></span>;
}
function Mastercard() {
  return <span className={card} title="Mastercard"><svg viewBox="0 0 38 24" className="h-5 w-auto"><circle cx="14" cy="12" r="10" fill="#EB001B" /><circle cx="24" cy="12" r="10" fill="#F79E1B" fillOpacity="0.9" /></svg></span>;
}
function Cb() {
  return <span className={card} title="Carte Bancaire"><svg viewBox="0 0 34 20" className="h-4 w-auto"><rect width="34" height="20" rx="3" fill="#0F6FB4" /><text x="17" y="15" textAnchor="middle" fontFamily="Arial" fontWeight="700" fontSize="13" fill="#fff">CB</text></svg></span>;
}
function PayPal() {
  return <span className={card} title="PayPal"><svg viewBox="0 0 62 16" className="h-3.5 w-auto"><text x="0" y="13" fontFamily="Arial" fontWeight="700" fontStyle="italic" fontSize="15"><tspan fill="#003087">Pay</tspan><tspan fill="#009CDE">Pal</tspan></text></svg></span>;
}

export function PaymentLogos({ className = "", testid = "payment-logos" }) {
  return (
    <div data-testid={testid} className={`flex flex-wrap items-center gap-2 ${className}`} aria-label="Moyens de paiement acceptés : Visa, Mastercard, CB, PayPal">
      <Visa /><Mastercard /><Cb /><PayPal />
    </div>
  );
}

export function RatingBadge({ className = "" }) {
  return (
    <span data-testid="rating-badge" className={`inline-flex items-center gap-2 ${className}`}>
      <span className="flex text-[var(--gold)]">{[0, 1, 2, 3, 4].map((i) => <Star key={i} className="h-3.5 w-3.5 fill-current" />)}</span>
      <span><b className="text-[var(--parchment)]">{SHOP.rating}/5</b> <T>note de nos clientes</T></span>
    </span>
  );
}

export function SecureNote({ className = "", testid = "secure-note" }) {
  return (
    <div data-testid={testid} className={`rounded-sm border border-[var(--line)] p-4 text-xs leading-relaxed text-[var(--parchment)]/85 ${className}`}>
      <p className="flex items-center gap-2 text-sm text-[var(--parchment)]"><Lock className="h-4 w-4 text-[var(--gold)]" /> <T>Paiement 100 % sécurisé</T></p>
      <p className="mt-2"><T>Connexion chiffrée (SSL). Vos données bancaires sont saisies chez Stripe ou PayPal, jamais sur notre site. Validation 3D Secure par votre banque.</T></p>
      <PaymentLogos className="mt-3" testid={`${testid}-logos`} />
      <p className="mt-3"><T>Une question avant de commander ? </T><a href={`tel:${SHOP.tel}`} data-testid={`${testid}-phone`} className="text-[var(--gold-soft)] underline underline-offset-4">{SHOP.phone}</a></p>
    </div>
  );
}

const PILLARS = [
  { icon: Lock, title: "Paiement 100 % sécurisé", text: "CB, Visa, Mastercard ou PayPal, avec 3D Secure" },
  { icon: Truck, title: "Livraison offerte", text: "En Point Relais ou Locker partout en France" },
  { icon: PackageCheck, title: SHOP.shipDelay, text: "Suivi du colis envoyé par email" },
  { icon: ShieldCheck, title: "Fabriqué en France", text: "Soins conçus par une marque familiale antillaise" },
  { icon: Phone, title: "Une vraie équipe à l'écoute", text: SHOP.phone, href: `tel:${SHOP.tel}` },
];

export function TrustStrip() {
  return (
    <section data-testid="trust-strip" className="border-y border-[var(--line)] bg-[var(--ink-2)] px-5 py-10 md:px-10">
      <ul className="mx-auto grid max-w-[1440px] gap-8 sm:grid-cols-2 lg:grid-cols-5">
        {PILLARS.map(({ icon: Icon, title, text, href }) => (
          <li key={title} className="flex items-start gap-3">
            <Icon className="mt-0.5 h-6 w-6 shrink-0 text-[var(--gold)]" />
            <div>
              <p className="text-sm font-medium text-[var(--parchment)]">{title}</p>
              {href ? <a href={href} data-testid="trust-strip-phone" className="text-sm text-[var(--gold-soft)] underline-offset-4 hover:underline">{text}</a> : <p className="text-sm text-[var(--parchment)]/75">{text}</p>}
            </div>
          </li>
        ))}
      </ul>
      <div className="mx-auto mt-8 flex max-w-[1440px] flex-wrap items-center justify-between gap-4 border-t border-[var(--line)] pt-6 text-sm text-[var(--parchment)]/80">
        <RatingBadge />
        <PaymentLogos testid="trust-strip-logos" />
        <Link to="/livraison" data-testid="trust-strip-delivery-link" className="text-[var(--gold-soft)] underline underline-offset-4">Livraison & paiement</Link>
      </div>
    </section>
  );
}
